// @ts-check
import { computed, reactive, toRef } from 'vue';

/** @typedef {import('@/data/schema/parties').TNewOrSavedParty} TUpdateParty */
/** @typedef {import('@/data/schema/parties').TNewOrSavedParties} TUpdateParties */
/** @typedef {import('@/data/schema/documents').TDocuments} TDocuments */

/**
 * @param {import('vue').Ref<TUpdateParties>} parties
 * @param {import('vue').Ref<TDocuments>} documents
 */
export function useRoles(parties, documents) {
  const documentsWithRoles = computed(() =>
    documents.value.filter(document => document.parties.length));

  const documentsWithoutRoles = computed(() =>
    documents.value.filter(document => !document.parties.length));

  const allRoles = computed(() =>
    documents.value.flatMap(document => document.parties));
  const assignedRoles = computed(() =>
    parties.value.flatMap(party => party.role_ids));
  const unassignedRoles = computed(() => allRoles.value.length - assignedRoles.value.length);

  /** @param {number} index - party index */
  function isCopyOnly(index) {
    const party = parties.value[index];
    return party.role === 'copy';
  }

  /** @param {number} id - role id */
  function isRoleCopy(id) {
    const role = allRoles.value.find(r => r.id === id);
    return role?.role === 'copy';
  }

  /**
   * @param {number} index - party index
   * @param {number} id - role id
   */
  function hasRole(index, id) {
    const party = parties.value[index];
    return party.role_ids.includes(id);
  }

  /** @param {number} index - party index */
  function hasSignerRole(index) {
    const party = parties.value[index];
    return party.role_ids.some(id => allRoles.value.find(r => r.id === id)?.role === 'signer');
  }

  /**
   * @param {number} index - party index
   * @param {number} id
   */
  function availableRole(index, id) {
    return hasRole(index, id) || !assignedRoles.value.includes(id);
  }

  /**
   * Get the party index that a role is assigned to
   *
   * @param {number} id - role id
   * @returns {number} party index
   */
  function assignedTo(id) {
    return parties.value.findIndex(party => party.role_ids.includes(id));
  }

  return reactive({
    allRoles,
    documentsWithRoles,
    documentsWithoutRoles,
    unassignedRoles,
    isCopyOnly,
    isRoleCopy,
    hasRole,
    hasSignerRole,
    availableRole,
    assignedTo,
  });
}

/** @typedef {ReturnType<typeof useRoles>} TRoles */

/**
 * @param {import('vue').Ref<TUpdateParties>} parties
 * @param {import('vue').Ref<TDocuments>} documents
 */
export function useAssignRoles(parties, documents) {
  const reactiveRoles = useRoles(parties, documents);
  const { hasRole, hasSignerRole } = reactiveRoles;
  const allRoles = toRef(reactiveRoles, 'allRoles');

  /** Get role ids grouped by document */
  const rolesByDocument = computed(() =>
    documents.value.map(document =>
      document.parties.map(party => party.id)));

  /**
   * @param {number} index - party index
   * @param {number} id
   */
  function unassignRole(index, id) {
    const party = parties.value[index];
    const roleIndex = party.role_ids.indexOf(id);

    if (roleIndex > -1) {
      party.role_ids.splice(roleIndex, 1);
    }
  }

  /**
   * @param {number} index - party index
   * @param {number} id - role id
   */
  function assignRole(index, id) {
    const party = parties.value[index];
    const role = allRoles.value.find(r => r.id === id);

    // unassign the role from any other parties
    parties.value.forEach((_, partyIndex) => unassignRole(partyIndex, id));

    // get all the roles on the document that contains this role
    const roleIds = rolesByDocument.value.find(roles => roles.includes(id));
    if (roleIds) {
      // unassign any the other roles on the document from this party
      roleIds.forEach(roleId => unassignRole(index, roleId));
    }

    // assign the role to this party
    party.role_ids.push(id);

    // if the party was a copy party and the assigned role isn't, change it to a signer
    if (party.role === 'copy' && role?.role !== 'copy') {
      party.role = 'signer';
    }

    // if the party was a signer and doesn't have any singer roles and the assigned role is a copy, change it to a copy
    if (
      party.role === 'signer' &&
      !hasSignerRole(index) &&
      role?.role === 'copy'
    ) {
      party.role = 'copy';
    }
  }

  /**
   * @param {number} index - party index
   * @param {number} id - role id
   */
  function toggleRoleSelected(index, id) {
    if (hasRole(index, id)) {
      unassignRole(index, id);
    } else {
      assignRole(index, id);
    }
  }

  /** @param {number} index - party index */
  function toggleCopyOnly(index) {
    const party = parties.value[index];

    if (party.role === 'signer' && !hasSignerRole(index)) {
      party.role = 'copy';
    } else {
      party.role = 'signer';
    }
  }

  /**
   * Assigns the only role to the only party when there is only one role and one party.
   */
  function assignOnlyRoleToOnlyParty() {
    if (allRoles.value.length === 1 && parties.value.length === 1) {
      assignRole(0, allRoles.value[0].id);
    }
  }

  return {
    toggleRoleSelected,
    toggleCopyOnly,
    assignOnlyRoleToOnlyParty,
  };
}
